import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/common';
import { ROUTES } from '../constants';
import { snapEarnService, ApiError } from '../services';
import moment from 'moment';

const OneSnapTransactionDetailPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const imageRef = useRef(null);

  // Get transaction from state or use dummy data
  const transactionFromState = location.state?.transaction;

  // Merge transaction data - receiptImages from API/list (files → URLs)
  const mergeTransactionData = transaction => {
    if (!transaction) return dummyTransaction;
    // API list returns "files" [{ fileName, fileType }]; detail/transform uses "receiptImages" (URLs)
    const receiptImages =
      transaction.receiptImages?.length > 0
        ? transaction.receiptImages
        : (transaction.files || []).map(f => f.fileName).filter(Boolean);
    return {
      ...transaction,
      receiptImages,
    };
  };

  // State management
  const [transactionData, setTransactionData] = useState(
    mergeTransactionData(transactionFromState || dummyTransaction)
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copiedDuplicateId, setCopiedDuplicateId] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Handle copying duplicate transaction ID to clipboard
  const handleCopyDuplicateId = useCallback((idToCopy) => {
    if (!idToCopy) return;

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(idToCopy).then(() => {
        setCopiedDuplicateId(true);
        setTimeout(() => setCopiedDuplicateId(false), 2000);
      }).catch(err => {
        console.error('Failed to copy to clipboard:', err);
      });
    } else {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = idToCopy;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setCopiedDuplicateId(true);
        setTimeout(() => setCopiedDuplicateId(false), 2000);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
    }
  }, []);
  // Determine initial status based on transactionData
  // transactionData.status comes from service transform which returns 'Approved', 'Pending', 'Rejected'
  const getInitialStatus = () => {
    const statusLower = (transactionData.status || '').toLowerCase();
    if (statusLower === 'approved' || statusLower === 'a') return 'approve';
    if (statusLower === 'rejected' || statusLower === 'r') return 'reject';
    return '';
  };

  const [transactionStatus, setTransactionStatus] = useState(
    getInitialStatus()
  );
  const [rejectReason, setRejectReason] = useState(
    transactionData.rejectedReasonId || ''
  );
  const [editedDate, setEditedDate] = useState(
    transactionData?.transactionDateTime
      ? moment(transactionData.transactionDateTime).format('YYYY-MM-DD')
      : ''
  );
  const [editedAmount, setEditedAmount] = useState(
    transactionData?.transactionAmount || 0
  );
  const [receiptInvoiceNumber, setReceiptInvoiceNumber] = useState(
    transactionData?.receiptNumber || ''
  );
  const [internalNotes, setInternalNotes] = useState(
    transactionData?.notes || ''
  );
  const [applied, setApplied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [otherReasonDescription, setOtherReasonDescription] = useState(
    transactionData.rejectedReasonDescription || ''
  );

  // Image drag state
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Reject reason options from API
  const [rejectReasons, setRejectReasons] = useState([]);
  const [loadingReasons, setLoadingReasons] = useState(false);

  // Fetch rejected reasons from API
  const fetchRejectedReasons = useCallback(async () => {
    setLoadingReasons(true);
    try {
      const reasons = await snapEarnService.getFormattedRejectedReasons();
      setRejectReasons(reasons);
    } catch (err) {
      console.error('Failed to fetch rejected reasons:', err);
      // Fallback to empty array on error - dropdown will show no options
      setRejectReasons([]);
    } finally {
      setLoadingReasons(false);
    }
  }, []);

  // Fetch rejected reasons on component mount
  useEffect(() => {
    fetchRejectedReasons();
  }, [fetchRejectedReasons]);

  // Effect to set reject reason specific logic once reasons are loaded
  useEffect(() => {
    if (rejectReasons.length > 0 && transactionStatus === 'reject') {
      // If we have a description but no ID (or ID not in list), try to find 'Other'
      if (transactionData.rejectedReasonDescription && !rejectReason) {
        const otherReason = rejectReasons.find(r => r.description === 'Other');
        if (otherReason) {
          setRejectReason(otherReason.id);
        }
      }
    }
  }, [rejectReasons, transactionStatus, transactionData, rejectReason]);

  // Image navigation handlers
  const handlePreviousImage = () => {
    setCurrentImageIndex(prev =>
      prev === 0 ? transactionData.receiptImages.length - 1 : prev - 1
    );
    setZoomLevel(1);
    setDragPosition({ x: 0, y: 0 });
  };

  const handleNextImage = () => {
    setCurrentImageIndex(prev =>
      prev === transactionData.receiptImages.length - 1 ? 0 : prev + 1
    );
    setZoomLevel(1);
    setDragPosition({ x: 0, y: 0 });
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => {
      const newZoom = Math.max(prev - 0.25, 1);
      if (newZoom === 1) {
        setDragPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  };

  // Drag handlers
  const handleMouseDown = e => {
    if (zoomLevel > 1) {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({
        x: e.clientX - dragPosition.x,
        y: e.clientY - dragPosition.y,
      });
    }
  };

  const handleMouseMove = e => {
    if (isDragging && zoomLevel > 1 && imageRef.current) {
      e.preventDefault();

      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;

      // Calculate bounds
      // The image is scaled from the center, so the maximum translation in either direction
      // is half of the total overflow width/height.
      // overflowWidth = (width * zoomLevel) - width = width * (zoomLevel - 1)
      // maxTranslate = overflowWidth / 2
      const { offsetWidth: width, offsetHeight: height } = imageRef.current;
      const maxTranslateX = (width * (zoomLevel - 1)) / 2;
      const maxTranslateY = (height * (zoomLevel - 1)) / 2;

      // Clamp values
      const clampedX = Math.min(Math.max(newX, -maxTranslateX), maxTranslateX);
      const clampedY = Math.min(Math.max(newY, -maxTranslateY), maxTranslateY);

      setDragPosition({
        x: clampedX,
        y: clampedY,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Detect if receipt URL is PDF (by extension or path)
  const isPdfUrl = url => {
    if (!url || typeof url !== 'string') return false;
    try {
      const path = new URL(url, window.location.origin).pathname;
      return /\.pdf(\?|$)/i.test(path) || path.toLowerCase().includes('.pdf');
    } catch {
      return /\.pdf(\?|$)/i.test(url) || url.toLowerCase().includes('.pdf');
    }
  };

  const currentReceiptUrl =
    transactionData.receiptImages?.[currentImageIndex];
  const currentIsPdf = currentReceiptUrl ? isPdfUrl(currentReceiptUrl) : false;

  // Form handlers
  const handleStatusChange = status => {
    setTransactionStatus(status);
    if (status === 'approve') {
      setRejectReason('');
    }
  };

  const handleCancel = () => {
    const returnSearch = location.state?.returnSearch || '';
    navigate(ROUTES.ONE_SNAP + returnSearch);
  };

  const [isQuotaExceededError, setIsQuotaExceededError] = useState(false);

  // Check if apply button should be visible
  const isApplyVisible = () => {
    // If transaction ocr not success, hide apply
    if (transactionData.ocrStatus !== 'S')
      return false;
    // If transaction is already approved, hide apply
    if (transactionData.status === 'Approved' || transactionData.status === 'A')
      return false;
    // If transaction is already approved, hide apply
    if (transactionData.status === 'Rejected' || transactionData.status === 'R')
      return false;
    return true;
  };

  // Check if save button should be enabled
  const isSaveEnabled = () => {
    // If quota exceeded error occurred, disable save
    if (isQuotaExceededError) return false;

    // If transaction is already approved, disable save
    if (transactionData.status === 'Approved' || transactionData.status === 'A')
      return false;

    // If transaction is already finalized (rejected/cancelled), disable save
    if (
      transactionData.status === 'Rejected' ||
      transactionData.status === 'R' ||
      transactionData.status === 'Cancelled' ||
      transactionData.status === 'C'
    )
      return false;

    if (!transactionStatus) return false;
    if (transactionStatus === 'reject' && !rejectReason) return false;

    const selectedReason = rejectReasons.find(r => r.id == rejectReason);
    if (
      transactionStatus === 'reject' &&
      selectedReason?.description === 'Other' &&
      !otherReasonDescription.trim()
    ) {
      return false;
    }

    return true;
  };

  // Format amount with . as decimal separator
  const formatAmount = amount => {
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setIsQuotaExceededError(false);

    try {
      // Map status to API format (A: Approve, R: Reject)
      const apiStatus = transactionStatus === 'approve' ? 'A' : 'R';
      console.log(editedDate);

      // Preserve original time when updating date
      const dateToSend = moment(editedDate, 'YYYY-MM-DD');
      if (transactionData?.transactionDateTime) {
        const originalTime = moment(transactionData.transactionDateTime);
        dateToSend.set({
          hour: originalTime.hour(),
          minute: originalTime.minute(),
          second: originalTime.second(),
        });
      }

      const payload = {
        snapEarnId: transactionData.id,
        transactionDate: dateToSend.format(),
        status: apiStatus,
        amount: Number(editedAmount),
        receiptNumber: receiptInvoiceNumber.trim(),
        notes: internalNotes.trim(),
      };

      // Add reason if rejected
      if (apiStatus === 'R') {
        payload.rejectedReasonId = rejectReason;
        const selectedReason = rejectReasons.find(r => r.id == rejectReason);
        if (selectedReason && selectedReason.description === 'Other') {
          payload.rejectedReasonDescription = otherReasonDescription;
        }
      }

      await snapEarnService.processApproval(payload);

      // Show success message (in production, use toast/notification)
      alert('Transaction updated successfully!');

      // Navigate back to list (preserve filter from URL)
      const returnSearch = location.state?.returnSearch || '';
      navigate(ROUTES.ONE_SNAP + returnSearch);
    } catch (err) {
      console.error('Failed to update transaction:', err);

      let errorMessage = 'Failed to update transaction. Please try again.';

      if (err instanceof ApiError) {
        errorMessage = err.message;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      // Handle Quota exceeded specifically
      if (
        errorMessage &&
        errorMessage.toLowerCase().includes('quota exceeded')
      ) {
        setTransactionStatus('reject');

        // Find reason for "Quota Exceeded"
        const quotaReason = rejectReasons.find(
          r => r.description.toLowerCase() === 'quota exceeded'
        );
        if (quotaReason) {
          setRejectReason(quotaReason.id);
        }

        setIsQuotaExceededError(true);
        // Use setTimeout to allow UI to update (radio and dropdown) before alert blocks execution
        setTimeout(() => {
          alert('Quota Exceeded');
          const returnSearch = location.state?.returnSearch || '';
          navigate(ROUTES.ONE_SNAP + returnSearch);
        }, 100);
      }

      setError(errorMessage);
      if (typeof window !== 'undefined' && window.scrollTo) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } finally {
      setSaving(false);
    }
  };
  const handleApply = useCallback(async () => {
    setApplied(true)
    setEditedAmount(transactionData.ocrTotalEuro)
    // setEditedDate(moment(transactionData.ocrDate).format('YYYY-MM-DD'))
    setReceiptInvoiceNumber(transactionData.ocrInvoiceNumber)

  });
  // Get status badge style
  const getStatusBadgeStyle = status => {
    switch (status) {
      case 'Approved':
        return 'border-2 border-solid border-green-200 bg-green-100 text-green-800';
      case 'Pending':
        return 'border-2 border-solid border-yellow-200 bg-yellow-100 text-yellow-800';
      case 'Pending Review':
        return 'border-2 border-solid border-yellow-200 bg-yellow-100 text-yellow-800';
      case 'Rejected':
        return 'border-2 border-solid border-red-200 bg-red-100 text-red-800';
      case 'Cancelled':
        return 'border-2 border-solid border-orange-200 bg-orange-100 text-orange-800';
      default:
        return 'border-2 border-solid border-gray-200 bg-gray-100 text-gray-800';
    }
  };
  const getConfidenceBadgeStyle = value => {
    if (value < 0.33) {
      return 'border-2 border-solid border-red-200 bg-red-100 text-red-800';
    } else if (value < 0.66) {
      return 'border-2 border-solid border-yellow-200 bg-yellow-100 text-yellow-800';
    } else {
      return 'border-2 border-solid border-green-200 bg-green-100 text-green-800';
    }
  };
  const getReiskBadgeStyle = value => {
    if (value < 0.33) {
      return 'border-2 border-solid border-green-200 bg-green-100 text-green-800';
    } else if (value < 0.66) {
      return 'border-2 border-solid border-yellow-200 bg-yellow-100 text-yellow-800';
    } else {
      return 'border-2 border-solid border-red-200 bg-red-100 text-red-800';
    }
  };
  const display = value => {
    if (value === null || value === undefined) return '-';
    const text = String(value).trim();
    return text && text !== 'N/A' ? text : '-';
  };
  const Field = ({ label, value, className = '', mono = false, type = '' }) => (
    <div className={className}>
      <p className='text-xs text-gray-500 mb-1'>{label}</p>
      {type == '' && <p
        className={`text-sm text-gray-900 break-words ${mono ? 'font-mono' : ''
          }`}
      >
        {display(value)}
      </p>}
      {type == 'confidence' && (
        <span
          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getConfidenceBadgeStyle(
            value
          )}`}
        >
          {value * 100}%
        </span>
      )}
      {type == 'risk' && (
        <span
          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getReiskBadgeStyle(
            value
          )}`}
        >
          {value * 100}%
        </span>
      )}
    </div>
  );
  const SectionHeader = ({ title }) => (
    <div className='mb-5'>
      <div className='flex items-center gap-2'>
        <h3 className='text-base font-semibold text-gray-900'>{title}</h3>
      </div>
      {/* TODO(BE-wiring): replace static placeholder with audit data */}
      {/* <p className='text-xs text-gray-500 mt-0.5'>
        Last Modified{' '}
        <span className='text-gray-700 font-medium'>{lastModifiedText}</span>
      </p> */}
    </div>
  );
  return (
    <div>
      <PageHeader
        title='Edit Transaction'
        subtitle='Edit the business owner information and make sure the data is correct'
        breadcrumbs={[
          { name: 'Home', href: ROUTES.DASHBOARD },
          { name: 'Snap & Earn', href: ROUTES.ONE_SNAP },
          { name: 'Edit Transaction' },
        ]}
      />

      {/* Error Display */}
      {error && (() => {
        const duplicateMatch =
          typeof error === 'string'
            ? error.match(
                /^(.*?\b(?:duplicates? with|duplikat dengan|duplicato con|pendua dengan|dúblach le)\b)\s+([^\s.,;]+)(.*)$/i
              )
            : null;

        return (
          <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-6'>
            <div className='flex items-center'>
              <svg
                className='w-5 h-5 text-red-400 mr-2.5 flex-shrink-0'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
                />
              </svg>
              {duplicateMatch ? (
                <div className='text-sm text-red-700 flex flex-wrap items-center gap-1.5'>
                  <span>{duplicateMatch[1]}</span>
                  <span className='inline-flex items-center gap-1.5 bg-red-100 border border-red-300 rounded px-2 py-0.5 font-mono text-red-900 font-semibold'>
                    <span>{duplicateMatch[2]}</span>
                    <button
                      type='button'
                      onClick={() => handleCopyDuplicateId(duplicateMatch[2])}
                      className='inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs text-red-700 hover:text-red-900 bg-red-200/60 hover:bg-red-200 transition-colors focus:outline-none focus:ring-1 focus:ring-red-400'
                      title='Copy ID'
                    >
                      {copiedDuplicateId ? (
                        <>
                          <svg
                            className='w-3.5 h-3.5 text-emerald-600'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2.5}
                              d='M5 13l4 4L19 7'
                            />
                          </svg>
                          <span className='text-emerald-700 font-sans font-medium'>Copied!</span>
                        </>
                      ) : (
                        <>
                          <svg
                            className='w-3.5 h-3.5 text-red-600'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2z'
                            />
                          </svg>
                          <span className='font-sans font-normal text-xs'>Copy</span>
                        </>
                      )}
                    </button>
                  </span>
                  {duplicateMatch[3] && <span>{duplicateMatch[3]}</span>}
                </div>
              ) : (
                <p className='text-sm text-red-700'>{error}</p>
              )}
            </div>
          </div>
        );
      })()}
      {/* {transactionData.companyName != 'Everywhere & Everyday' && ( */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200 mb-6'>
        <div className='gap-3 p-6'>
          <section>
            <SectionHeader
              title='OCR Result'
            />
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5'>
              <Field label='Brand name' value={transactionData.ocrMerchantName} />
              <Field label='Outlet' value={transactionData.ocrMerchantAddress} />
              <Field label='Merchant VAT number' value={transactionData.ocrMerchantVatNumber} />
              <Field label='Receipt/Invoice number' value={transactionData.ocrInvoiceNumber} />
              <Field label='Transaction date' value={transactionData.ocrDate ? moment(transactionData.ocrDate).format('DD MMM YYYY') : '-'} />
              <Field label='Total transaction amount' value={transactionData.ocrTotal} />
              <Field label='Currency' value={transactionData.ocrCurrency} />
              <Field label='Risk Score' value={transactionData.ocrRiskScore} type="risk" />
              <Field label='Confidence level' value={transactionData.ocrFraudConfidence} type="confidence" />

            </div>
            {transactionData.ocrFraudMessage && (
              <div
                className={`p-2 mt-5 text-xs font-medium rounded-xl border-2 border-solid border-red-200 bg-red-100 text-red-800`}
              >
                {transactionData.ocrFraudMessage}
              </div>
            )}
            {isApplyVisible() && (
              <div className='flex justify-end mt-5'>
                <button
                  type='button'
                  onClick={handleApply}
                  disabled={applied}
                  className='bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed'
                >
                  Apply OCR Result
                </button>
              </div>
            )}
          </section>

        </div>
      </div>
      {/* )} */}
      {/* Main Content */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
        <div className='grid grid-cols-1 lg:grid-cols-5 gap-6 p-6'>
          {/* Left Section - Receipt Images */}
          <div className='lg:col-span-2'>
            <div className='mb-5'>
              <div className='flex items-center gap-2'>
                <h3 className='text-base font-semibold text-gray-900'>Receipt Image</h3>
              </div>
            </div>

            <div className='bg-gray-100 rounded-lg p-4 min-h-full'>
              {/* Image Display */}
              <div
                className='relative bg-white rounded-lg overflow-hidden mb-4'
                onMouseMove={currentIsPdf ? undefined : handleMouseMove}
                onMouseUp={currentIsPdf ? undefined : handleMouseUp}
                onMouseLeave={currentIsPdf ? undefined : handleMouseLeave}
              >
                {transactionData.receiptImages &&
                  transactionData.receiptImages.length > 0 ? (
                  currentIsPdf ? (
                    <>
                      {/* Mobile: card + link (iframe often doesn't render PDF on mobile) */}
                      <div className='flex lg:hidden flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-6 min-h-[12rem]'>
                        <svg className='w-12 h-12 text-red-500 mb-3 shrink-0' fill='currentColor' viewBox='0 0 20 20' aria-hidden='true'>
                          <path fillRule='evenodd' d='M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z' clipRule='evenodd' />
                        </svg>
                        <p className='text-sm font-medium text-gray-700 mb-1'>PDF document</p>
                        <a
                          href={currentReceiptUrl}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700'
                        >
                          <span>Open PDF</span>
                          <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14' /></svg>
                        </a>
                      </div>
                      {/* Desktop: embedded iframe */}
                      <iframe
                        src={currentReceiptUrl}
                        title={`Receipt ${currentImageIndex + 1} (PDF)`}
                        className='hidden lg:block w-full min-h-[50rem] border-0 rounded-lg bg-gray-100'
                        style={{ height: 'min(50rem, 75vh)' }}
                      />
                    </>
                  ) : (
                    <img
                      ref={imageRef}
                      src={transactionData.receiptImages[currentImageIndex]}
                      alt={`Receipt ${currentImageIndex + 1}`}
                      onMouseDown={handleMouseDown}
                      className={`w-full h-full object-contain ${isDragging ? '' : 'transition-transform duration-200'
                        }`}
                      style={{
                        transform: `translate(${dragPosition.x}px, ${dragPosition.y}px) scale(${zoomLevel})`,
                        cursor:
                          zoomLevel > 1
                            ? isDragging
                              ? 'grabbing'
                              : 'grab'
                            : 'default',
                      }}
                    />
                  )
                ) : (
                  <div className='flex items-center justify-center h-96 bg-gray-200'>
                    <p className='text-gray-500'>No receipt file available</p>
                  </div>
                )}

                {/* Zoom Icons - only for images */}
                {!currentIsPdf && (
                  <div className='absolute bottom-4 right-4 flex gap-2'>
                    <button
                      onClick={handleZoomIn}
                      className='bg-white rounded-full p-2 shadow-lg hover:bg-gray-50'
                      title='Zoom in'
                    >
                      <svg
                        className='w-5 h-5 text-gray-700'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7'
                        />
                      </svg>
                    </button>
                    <button
                      onClick={handleZoomOut}
                      className='bg-white rounded-full p-2 shadow-lg hover:bg-gray-50'
                      title='Zoom out'
                    >
                      <svg
                        className='w-5 h-5 text-gray-700'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7'
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              {/* File counter & navigation (images + PDFs) */}
              {transactionData.receiptImages &&
                transactionData.receiptImages.length > 1 && (
                  <div className='flex items-center justify-between'>
                    <button
                      onClick={handlePreviousImage}
                      className='p-2 rounded-full hover:bg-gray-200'
                    >
                      <svg
                        className='w-6 h-6 text-gray-700'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M15 19l-7-7 7-7'
                        />
                      </svg>
                    </button>

                    <span className='text-sm text-gray-600'>
                      file {currentImageIndex + 1} of{' '}
                      {transactionData.receiptImages.length}
                    </span>

                    <button
                      onClick={handleNextImage}
                      className='p-2 rounded-full hover:bg-gray-200'
                    >
                      <svg
                        className='w-6 h-6 text-gray-700'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M9 5l7 7-7 7'
                        />
                      </svg>
                    </button>
                  </div>
                )}
            </div>
          </div>

          {/* Right Section - Form Fields */}
          <div className='lg:col-span-3 space-y-4'>
            <div className='mb-5'>
              <div className='flex justify-between items-center'>
                <h3 className='text-base font-semibold text-gray-900'>Business Validation</h3>
                <div className='text-right'>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeStyle(
                      transactionData.status
                    )}`}
                  >
                    {transactionData.status}
                  </span>
                </div>
              </div>
            </div>
            {/* Company Name (Read-only) */}
            <div>
              <label className='block text-sm font-medium text-gray-500 mb-1'>
                Company name
              </label>
              <div className='bg-gray-200 px-3 py-2 rounded-md border border-gray-200'>
                <p className='text-sm text-gray-500'>
                  {transactionData.companyName}
                </p>
              </div>
            </div>

            {/* Legal Business Name (Read-only) */}
            <div>
              <label className='block text-sm font-medium text-gray-500 mb-1'>
                Legal Business Name
              </label>
              <div className='bg-gray-200 px-3 py-2 rounded-md border border-gray-200'>
                <p className='text-sm text-gray-500'>
                  {transactionData.legalBusinessName || '-'}
                </p>
              </div>
            </div>

            {/* Brand Name & Tax ID/VAT Number (Read-only, side by side) */}
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-500 mb-1'>
                  Brand Name
                </label>
                <div className='bg-gray-200 px-3 py-2 rounded-md border border-gray-200'>
                  <p className='text-sm text-gray-500'>
                    {transactionData.brandName || '-'}
                  </p>
                </div>
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-500 mb-1'>
                  Tax ID/VAT Number
                </label>
                <div className='bg-gray-200 px-3 py-2 rounded-md border border-gray-200'>
                  <p className='text-sm text-gray-500'>{transactionData.taxId || '-'}</p>
                </div>
              </div>
            </div>

            {/* Sector & Outlet (Read-only, side by side) */}
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-500 mb-1'>
                  Sector
                </label>
                <div className='bg-gray-200 px-3 py-2 rounded-md border border-gray-200'>
                  <p className='text-sm text-gray-500'>
                    {transactionData.sector}
                  </p>
                </div>
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-500 mb-1'>
                  Outlet
                </label>
                <div className='bg-gray-200 px-3 py-2 rounded-md border border-gray-200'>
                  <p className='text-sm text-gray-500'>
                    {transactionData.outlet}
                  </p>
                </div>
              </div>
            </div>

            {/* Shopper Name (Read-only) */}
            <div>
              <label className='block text-sm font-medium text-gray-500 mb-1'>
                Shopper name
              </label>
              <div className='bg-gray-200 px-3 py-2 rounded-md border border-gray-200'>
                <p className='text-sm text-gray-500'>
                  {transactionData.shopperName}
                </p>
              </div>
            </div>

            {/* Shopper Phone Number (Read-only, full width) */}
            <div>
              <label className='block text-sm font-medium text-gray-500 mb-1'>
                Shopper phone number
              </label>
              <div className='bg-gray-200 px-3 py-2 rounded-md border border-gray-200'>
                <p className='text-sm text-gray-500'>
                  {transactionData.shopperPhone.replace('+62', '')}
                </p>
              </div>
            </div>

            {/* Receipt/Invoice Number (Editable) */}
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>
                Receipt/Invoice Number
              </label>
              <input
                type='text'
                value={receiptInvoiceNumber}
                onChange={e => {
                  const value = e.target.value;
                  if (value.length <= 30) {
                    setReceiptInvoiceNumber(value);
                  }
                }}
                placeholder='Enter receipt/invoice number'
                maxLength={30}
                className='block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              />
            </div>

            {/* Internal Notes (Editable) */}
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>
                Internal Notes
              </label>
              <textarea
                value={internalNotes}
                onChange={e => setInternalNotes(e.target.value)}
                placeholder='Enter internal notes...'
                rows={3}
                className='block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none'
              />
            </div>

            {/* Transaction Date & Amount (Editable, side by side) */}
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Transaction date <span className='text-red-500'>*</span>
                </label>
                <input
                  type='date'
                  value={editedDate}
                  onChange={e => setEditedDate(e.target.value)}
                  // min={(() => {
                  //   const yesterday = new Date();
                  //   yesterday.setDate(yesterday.getDate() - 1);
                  //   return yesterday.toISOString().split('T')[0];
                  // })()}
                  max={new Date().toISOString().split('T')[0]}
                  className='block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Transaction amount <span className='text-red-500'>*</span>
                </label>
                <div className='relative'>
                  <input
                    type='text'
                    value={editedAmount}
                    onChange={e => {
                      const value = e.target.value;
                      // Allow only numbers and single decimal point
                      if (value === '' || /^\d*\.?\d*$/.test(value)) {
                        setEditedAmount(value === '' ? '' : value);
                      }
                    }}
                    onBlur={e => {
                      // Convert to number on blur to ensure proper formatting
                      const numValue = parseFloat(e.target.value) || 0;
                      setEditedAmount(numValue);
                    }}
                    placeholder='0.00'
                    className='block w-full px-3 py-2 pr-12 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  />
                  <div className='absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none'>
                    <span className='text-gray-500 text-sm'>EUR</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Transaction Status */}
            <div className='pt-4 border-t border-gray-200'>
              <label className='block text-sm font-medium text-gray-700 mb-3'>
                Transaction Status
              </label>
              <div className='flex gap-6'>
                <label className='flex items-center cursor-pointer'>
                  <input
                    type='radio'
                    name='status'
                    value='approve'
                    checked={transactionStatus === 'approve'}
                    onChange={e => handleStatusChange(e.target.value)}
                    className='w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500'
                  />
                  <span className='ml-2 text-sm text-gray-900'>Approve</span>
                </label>
                <label className='flex items-center cursor-pointer'>
                  <input
                    type='radio'
                    name='status'
                    value='reject'
                    checked={transactionStatus === 'reject'}
                    onChange={e => handleStatusChange(e.target.value)}
                    className='w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500'
                  />
                  <span className='ml-2 text-sm text-gray-900'>Reject</span>
                </label>
              </div>
            </div>

            {/* TODO */}
            {/* Reject Reason (Conditional) */}
            {transactionStatus === 'reject' && (
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Reason
                </label>
                <select
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  disabled={loadingReasons}
                  className='block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  <option value=''>
                    {loadingReasons ? 'Loading reasons...' : 'Select reason...'}
                  </option>
                  {rejectReasons.map(reason => (
                    <option key={reason.id} value={reason.id}>
                      {reason.description}
                    </option>
                  ))}
                </select>

                {rejectReasons.find(r => r.id == rejectReason)?.description ===
                  'Other' && (
                    <div className='mt-3'>
                      <label className='block text-sm font-medium text-gray-700 mb-1'>
                        Short Description
                      </label>
                      <input
                        type='text'
                        value={otherReasonDescription}
                        onChange={e => setOtherReasonDescription(e.target.value)}
                        placeholder='Enter a short description...'
                        className='block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                      />
                    </div>
                  )}
              </div>
            )}

            {/* Transaction History */}
            <div className='pt-4 border-t border-gray-200'>
              <h3 className='text-sm font-medium text-gray-900 mb-3'>
                Transaction History
              </h3>
              <div className='border border-gray-200 rounded-lg p-4'>
                <div className='relative'>
                  {transactionData.histories
                    .sort((a, b) => new Date(b.at) - new Date(a.at))
                    .map((history, index) => (
                      <div
                        key={index}
                        className='flex items-start gap-3 pb-6 relative'
                      >
                        {/* Vertical line connector */}
                        {index < transactionData.histories.length - 1 && (
                          <div className='absolute left-3 top-6 bottom-0 w-px bg-gray-300'></div>
                        )}
                        {/* Icon based on status */}
                        {history.action.toLowerCase().includes('approved') && (
                          <div className='flex-shrink-0 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center relative z-10'>
                            <svg
                              width='8'
                              height='8'
                              viewBox='0 0 8 8'
                              fill='none'
                              xmlns='http://www.w3.org/2000/svg'
                            >
                              <path
                                d='M6.66732 2L3.00065 5.66667L1.33398 4'
                                stroke='white'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                            </svg>
                          </div>
                        )}

                        {history.action.toLowerCase().includes('rejected') && (
                          <div className='flex-shrink-0 w-6 h-6 rounded-full bg-red-500 flex items-center justify-center relative z-10'>
                            <svg
                              width='8'
                              height='8'
                              viewBox='0 0 8 8'
                              fill='none'
                              xmlns='http://www.w3.org/2000/svg'
                            >
                              <path
                                d='M6 2L2 6M2 2L6 6'
                                stroke='white'
                                strokeWidth='1.5'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                            </svg>
                          </div>
                        )}

                        {history.action.toLowerCase().includes('cancelled') && (
                          <div className='flex-shrink-0 w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center relative z-10'>
                            <svg
                              width='10'
                              height='10'
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='white'
                              strokeWidth='2.5'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            >
                              <path d='M3 7v6h6' />
                              <path d='M21 17a9 9 0 0 0-15-6.7L3 13' />
                            </svg>
                          </div>
                        )}

                        {(history.action == '' || history.action.toLowerCase().includes('submitted')) && (
                          <div className='flex-shrink-0 w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center relative z-10'>
                            <div className='w-2 h-2 rounded-full bg-gray-500'></div>
                          </div>
                        )}
                        <div className='flex-1'>
                          <p className='text-sm text-gray-900'>
                            {history.action ? ` ${history.action} - by [${history.by}]` : history.by}
                          </p>
                          <p className='text-xs text-gray-500 mt-0.5'>
                            {history.at
                              ? moment(history.at).format('D MMM YYYY • HH:mm')
                              : 'N/A'}
                          </p>
                          {history.action.toLowerCase().includes('cancelled') &&
                            transactionData.cancelledReasonDescription && (
                              <p className='text-xs text-gray-600 mt-1'>
                                Reason: {transactionData.cancelledReasonDescription}
                              </p>
                            )}
                          {history.action.toLowerCase().includes('rejected') &&
                            transactionData.rejectedReasonDescription && (
                              <p className='text-xs text-gray-600 mt-1'>
                                Reason: {transactionData.rejectedReasonDescription}
                              </p>
                            )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>

          <div className='lg:col-span-5 space-y-4'>
            {/* Action Buttons */}
            <div className='flex gap-3 pt-6 justify-center'>
              <button
                onClick={handleCancel}
                disabled={saving}
                className='w-32 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!isSaveEnabled() || saving}
                className='w-96 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OneSnapTransactionDetailPage;
